import { SectionContainer } from '@/modules/pmt/components/SectionContainer';
import { MOCK_CARRIER_REFERENCE_KEY, analyticsWithIssue } from '@/modules/pmt/constants';
import type { ICarrierWithFirstFoundEvent, IParcelEvent } from '@/modules/pmt/interfaces';
import { scrollIntoView } from '@/modules/pmt/utils';
import { type FC, useEffect, useState } from 'react';
import { TRACKING_DETAILS_SECTIONS } from '../../constants';
import {
  CarrierLogo,
  CircleIcon,
  Details,
  DetailsWrapper,
  EventDate,
  EventGroup,
  EventTime,
  IssueIcon,
  Location,
  Name,
  TrackingEventsContainer,
} from './TrackingEventList.styles';
import { type IModifiedEvent, useTrackingEventList } from './hooks';

interface ITrackingEventProps {
  event: IModifiedEvent;
  isMockCarrierShipment: boolean;
}

const TrackingEvent = ({ event }: ITrackingEventProps) => {
  const { time, name, location, analytics, carrier } = event;
  const hasIssueIcon = analytics && analyticsWithIssue.includes(analytics);

  const renderCarrierLogo = () => {
    // Ref: https://www.pivotaltracker.com/story/show/186795116
    if (!carrier?.logo || carrier.carrierId === MOCK_CARRIER_REFERENCE_KEY) {
      return null;
    }

    return (
      <CarrierLogo
        tabIndex={0}
        aria-label={`${carrier.name}, Click to scroll to the carrier`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            scrollIntoView(carrier.id);
          }
        }}
        onClick={() => scrollIntoView(carrier.id)}
        src={carrier.logo}
        alt={carrier.name}
      />
    );
  };

  return (
    <DetailsWrapper>
      <Details>
        <EventTime>{time}</EventTime>
        <Name>
          {name}
          {hasIssueIcon && <IssueIcon />}
        </Name>
        {location && <Location>{location}</Location>}
      </Details>
      {renderCarrierLogo()}
      <CircleIcon />
    </DetailsWrapper>
  );
};

interface ITrackingEventListProps {
  parcelId: string;
  eventList: IParcelEvent[];
  carrierList: ICarrierWithFirstFoundEvent[];
  isMockCarrierShipment: boolean;
}

export const TrackingEventList: FC<ITrackingEventListProps> = ({
  parcelId,
  eventList,
  carrierList,
  isMockCarrierShipment,
}) => {
  const sectionId = isMockCarrierShipment ? '' : parcelId;
  const [isGrown, setIsGrown] = useState(false);
  const [elementHeight, setElementHeight] = useState<number | undefined>();
  const [slicedEventList, setSlicedEventList] = useState<IParcelEvent[]>([]);
  const groups = useTrackingEventList({ carrierList, eventList: slicedEventList });

  // IMPROVEMENT: remove slicedEventList state.
  useEffect(() => {
    eventList.length <= 3 ? setSlicedEventList(eventList) : setSlicedEventList(eventList.slice(0, 3));
  }, [eventList]);

  const handleToggle = () => {
    setIsGrown((prev) => !prev);
    if (!isGrown) setSlicedEventList(eventList);
  };

  const showContent = !!groups.length;

  return (
    <SectionContainer>
      <SectionContainer.Header
        sectionId={sectionId} // Ref: https://www.pivotaltracker.com/story/show/186795116
        sectionIcon={TRACKING_DETAILS_SECTIONS.Tracking.icon}
        sectionTitle={TRACKING_DETAILS_SECTIONS.Tracking.titleKey}
        gutterBottom={showContent}
      />
      {showContent && (
        <SectionContainer.GrowableContent
          isGrown={isGrown}
          contentHeight={elementHeight}
          showToggle={eventList.length > 3}
          handleToggle={handleToggle}
        >
          <TrackingEventsContainer ref={(node) => setElementHeight(node?.scrollHeight)}>
            {groups.map(({ date, eventList: groupedList }) => (
              <EventGroup key={date}>
                <EventDate>{date}</EventDate>
                {groupedList.map((event) => (
                  <TrackingEvent key={event.id} event={event} isMockCarrierShipment={isMockCarrierShipment} />
                ))}
              </EventGroup>
            ))}
          </TrackingEventsContainer>
        </SectionContainer.GrowableContent>
      )}
    </SectionContainer>
  );
};
